/**
 * 路由工作台（Alice 模型表里行内展开的 .route-workbench）：按优先级分层展示降级链，每个节点可调优先级、同层权重、启停。
 *
 * 背景：一个对外模型对应一条降级链（model_routes）。数据面先用优先级数字最小的一层，同层按权重分流，
 *   当前层全部失败再落到下一层。Alice 的工作台把「层」画成泳道，节点是卡片。
 * 设计意图：
 *   - 保存是整组提交（PUT /models/{id}/routes），节点参数（request_overrides）原样带回，不在工作台里丢失；
 *     需要改节点参数时用「高级」打开完整的路由编辑弹窗（RoutesEditor）。
 *   - 新增节点只能从渠道模型库里选已登记、启用且类型一致的上游模型（后端同样校验）。
 * 关键约束：只有 admin 能保存；没有任何启用节点的模型调用会失败，页面用红字提示。
 */
import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useState } from 'react'
import { catalogApi, type CatalogChannel, type CatalogModel, type LibraryItem } from '@ap/api-client/admin-catalog'
import type { Route } from '@ap/api-client/types'
import { A } from '@ap/i18n/admin'
import { AG_catalog as G, errorTextCat } from '@ap/i18n/admin-catalog'
import { useI18n } from '@ap/i18n'
import { useToast } from '@ap/ui'
import { IosToggle } from '../../alice/kit-catalog.tsx'

interface Node {
  channel_id: string
  upstream_model: string
  priority: string
  weight: string
  enabled: boolean
  request_overrides: Record<string, unknown>
}

const toNode = (r: Route): Node => ({
  channel_id: r.channel_id,
  upstream_model: r.upstream_model,
  priority: String(r.priority),
  weight: String(r.weight),
  enabled: r.enabled,
  request_overrides: r.request_overrides ?? {},
})

export function RouteWorkbench({
  model,
  channels,
  library,
  isAdmin,
  colSpan,
  onSaved,
  onAdvanced,
}: {
  model: CatalogModel
  channels: CatalogChannel[]
  library: LibraryItem[]
  isAdmin: boolean
  colSpan: number
  onSaved: () => void
  onAdvanced: () => void
}) {
  const { t, locale } = useI18n()
  const toast = useToast()
  const [nodes, setNodes] = useState<Node[]>(() => model.routes.map(toNode))
  const [addChannel, setAddChannel] = useState(channels[0]?.id ?? '')
  const [addUpstream, setAddUpstream] = useState('')
  const [busy, setBusy] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')

  const channelOf = (id: string) => channels.find((c) => c.id === id)
  const upstreamsOf = (cid: string) => library.filter((m) => m.channel_id === cid && m.enabled && m.kind === model.kind).map((m) => m.upstream_model)
  const edit = (i: number, patch: Partial<Node>) => {
    setNodes((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))
    setDirty(true)
  }

  // 按优先级分层，层内保持原顺序；priority 不是数字的节点排最后（提交时会被拒绝并提示）
  const lanes = [...new Set(nodes.map((n) => (Number.isFinite(Number(n.priority)) ? Number(n.priority) : 1e9)))].sort((a, b) => a - b)

  const add = () => {
    const up = addUpstream || upstreamsOf(addChannel)[0]
    if (!addChannel || !up) return
    if (nodes.some((n) => n.channel_id === addChannel && n.upstream_model === up)) {
      setError(t(G.models.wbDuplicate))
      return
    }
    setError('')
    const lastPriority = nodes.length ? Math.max(...nodes.map((n) => Number(n.priority) || 0)) : 0
    setNodes((xs) => [...xs, { channel_id: addChannel, upstream_model: up, priority: String(nodes.length ? lastPriority + 1 : 0), weight: '100', enabled: true, request_overrides: {} }])
    setDirty(true)
  }

  const save = async () => {
    setBusy(true)
    setError('')
    try {
      const routes: Route[] = nodes.map((n) => {
        const priority = Number(n.priority)
        const weight = Number(n.weight)
        if (!Number.isInteger(priority) || priority < 0 || priority > 100) throw new Error(t(G.models.wbBadPriority))
        if (!Number.isInteger(weight) || weight < 1 || weight > 10000) throw new Error(t(G.models.wbBadWeight))
        return { channel_id: n.channel_id, upstream_model: n.upstream_model, priority, weight, enabled: n.enabled, request_overrides: n.request_overrides }
      })
      const r = await catalogApi.putRoutes(model.id, routes, '')
      toast.ok(t(G.models.wbSaved, { v: r.config_version }))
      setDirty(false)
      onSaved()
    } catch (e) {
      console.error('[models] 保存路由失败', e)
      setError(e instanceof Error && !('code' in e) ? e.message : errorTextCat(e, locale))
    } finally {
      setBusy(false)
    }
  }

  const enabledCount = nodes.filter((n) => n.enabled).length
  const upstreams = upstreamsOf(addChannel)

  return (
    <tr>
      <td colSpan={colSpan} style={{ padding: 0 }}>
        <div className="route-workbench">
          <div className="route-explain">
            <span>{t(G.models.wbExplain1)}</span>
            <span>{t(G.models.wbExplain2)}</span>
            <span>{t(G.models.wbExplain3)}</span>
          </div>
          {enabledCount === 0 && <div className="cat-banner error">{t(A.models.noRoutes)}</div>}
          {error && <div className="cat-banner error">{error}</div>}
          <div className="route-lanes">
            {lanes.map((lane, li) => (
              <div className="route-lane" key={lane}>
                <div className="route-lane-label">
                  <div className="route-lane-title">{t(G.models.wbLane, { n: li + 1 })}</div>
                  <div className="route-lane-sub">{t(G.models.wbLaneSub, { p: lane === 1e9 ? '?' : lane })}</div>
                </div>
                <div className="route-nodes">
                  {nodes.map((n, i) => {
                    const p = Number.isFinite(Number(n.priority)) ? Number(n.priority) : 1e9
                    if (p !== lane) return null
                    const ch = channelOf(n.channel_id)
                    const h = !ch || !ch.enabled ? '' : ch.health_status === 'auto_disabled' ? 'open' : ch.consecutive_failures > 0 ? 'degraded' : 'healthy'
                    return (
                      <div key={`${n.channel_id}/${n.upstream_model}`} className={`route-node${n.enabled ? '' : ' disabled'}`}>
                        <div className="route-node-head">
                          <span className={`health-dot ${h}`} />
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 12, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ch?.name || n.channel_id}</div>
                            <div className="mono" style={{ fontSize: 10, color: 'var(--text-tertiary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.upstream_model}>
                              {n.upstream_model}
                            </div>
                          </div>
                          <IosToggle on={n.enabled} disabled={!isAdmin} onChange={(v) => edit(i, { enabled: v })} />
                        </div>
                        <div className="route-node-controls">
                          <div>
                            <label>{t(G.models.wbPriority)}</label>
                            <input type="number" min={0} max={100} disabled={!isAdmin} value={n.priority} onChange={(e) => edit(i, { priority: e.target.value })} />
                          </div>
                          <div>
                            <label>{t(G.models.wbWeight)}</label>
                            <input type="number" min={1} max={10000} disabled={!isAdmin} value={n.weight} onChange={(e) => edit(i, { weight: e.target.value })} />
                          </div>
                          {isAdmin && (
                            <button
                              className="btn btn-secondary btn-sm"
                              title={t(G.models.wbRemove)}
                              onClick={() => {
                                setNodes((xs) => xs.filter((_, j) => j !== i))
                                setDirty(true)
                              }}
                            >
                              <IconTrash size={12} />
                            </button>
                          )}
                        </div>
                        {Object.keys(n.request_overrides).length > 0 && (
                          <div className="mono" style={{ marginTop: 6, fontSize: 10, color: 'var(--text-tertiary)' }} title={JSON.stringify(n.request_overrides)}>
                            {t(G.models.wbOverrides, { n: Object.keys(n.request_overrides).length })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
          {isAdmin && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 14 }}>
              <select
                className="cat-select"
                value={addChannel}
                onChange={(e) => {
                  setAddChannel(e.target.value)
                  setAddUpstream('')
                }}
              >
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name || c.id}
                  </option>
                ))}
              </select>
              <select className="cat-select" value={addUpstream || upstreams[0] || ''} onChange={(e) => setAddUpstream(e.target.value)} disabled={upstreams.length === 0}>
                {upstreams.length === 0 && <option value="">{t(G.models.wbNoUpstream)}</option>}
                {upstreams.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
              <button className="btn btn-secondary btn-sm" disabled={upstreams.length === 0 || nodes.length >= 50} onClick={add}>
                <IconPlus size={12} style={{ verticalAlign: -2, marginRight: 4 }} />
                {t(G.models.wbAdd)}
              </button>
              <span style={{ flex: 1 }} />
              <button className="btn btn-secondary btn-sm" onClick={onAdvanced}>
                {t(G.models.wbAdvanced)}
              </button>
              <button className="btn btn-primary btn-sm" disabled={busy || !dirty} onClick={save}>
                {t(G.models.wbSave)}
              </button>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}
